import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/authService';
import './Settings.css';

function Settings() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('password');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estados para cambio de contraseña
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  // Estados para cambio de email
  const [newEmail, setNewEmail] = useState('');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);

  // Estados para 2FA
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorCodeSent, setTwoFactorCodeSent] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await apiClient.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    fetchUser();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Cambio de contraseña
  const handleRequestPasswordChange = async () => {
    setLoading(true);
    try {
      await apiClient.post('/auth/request-password-change');
      setCodeSent(true);
      showMessage('success', 'Verification code sent to your email');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error sending verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (newPassword !== confirmPassword) {
      showMessage('error', 'Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        verification_code: verificationCode
      });
      showMessage('success', 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setVerificationCode('');
      setCodeSent(false);
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  // Cambio de email
  const handleRequestEmailChange = async () => {
    if (!newEmail) {
      showMessage('error', 'Please enter a new email address');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/auth/request-email-change', { email: newEmail });
      setEmailCodeSent(true);
      showMessage('success', 'Verification code sent to new email');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error sending verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiClient.post('/auth/change-email', {
        new_email: newEmail,
        verification_code: emailVerificationCode
      });
      showMessage('success', 'Email changed successfully');
      setNewEmail('');
      setEmailVerificationCode('');
      setEmailCodeSent(false);
      // Refresh user info
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error changing email');
    } finally {
      setLoading(false);
    }
  };

  // 2FA
  const handleRequest2FA = async () => {
    setLoading(true);
    try {
      await apiClient.post('/auth/request-2fa');
      setTwoFactorCodeSent(true);
      showMessage('success', 'Verification code sent to your email');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error sending verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiClient.post('/auth/enable-2fa', {
        verification_code: twoFactorCode
      });
      showMessage('success', '2FA enabled successfully');
      setTwoFactorCode('');
      setTwoFactorCodeSent(false);
      // Refresh user info
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error enabling 2FA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <h1 className="settings-title">Settings</h1>

      {message.text && (
        <div className={`settings-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          Change Password
        </button>
        <button
          className={`settings-tab ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          Change Email
        </button>
        <button
          className={`settings-tab ${activeTab === '2fa' ? 'active' : ''}`}
          onClick={() => setActiveTab('2fa')}
        >
          Two-Factor Authentication
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'password' && (
          <div className="settings-section">
            <h2>Change Password</h2>
            {!codeSent ? (
              <div>
                <p>Click the button below to receive a verification code via email.</p>
                <button
                  className="settings-button"
                  onClick={handleRequestPasswordChange}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleChangePassword}>
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    required
                    maxLength={6}
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="settings-button" disabled={loading}>
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            )}
          </div>
        )}

        {activeTab === 'email' && (
          <div className="settings-section">
            <h2>Change Email</h2>
            <p>Current email: <strong>{user?.email}</strong></p>
            {!emailCodeSent ? (
              <form onSubmit={(e) => { e.preventDefault(); handleRequestEmailChange(); }}>
                <div className="form-group">
                  <label>New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="settings-button" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleChangeEmail}>
                <div className="form-group">
                  <label>New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    required
                    maxLength={6}
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="settings-button" disabled={loading}>
                  {loading ? 'Changing...' : 'Change Email'}
                </button>
              </form>
            )}
          </div>
        )}

        {activeTab === '2fa' && (
          <div className="settings-section">
            <h2>Two-Factor Authentication</h2>
            <p>Status: <strong>{user?.two_factor_enabled ? 'Enabled' : 'Disabled'}</strong></p>
            {!user?.two_factor_enabled ? (
              !twoFactorCodeSent ? (
                <div>
                  <p>Enable two-factor authentication for additional security.</p>
                  <button
                    className="settings-button"
                    onClick={handleRequest2FA}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleEnable2FA}>
                  <div className="form-group">
                    <label>Verification Code</label>
                    <input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      placeholder="Enter 6-digit code"
                      required
                      maxLength={6}
                      disabled={loading}
                    />
                  </div>
                  <button type="submit" className="settings-button" disabled={loading}>
                    {loading ? 'Enabling...' : 'Enable 2FA'}
                  </button>
                </form>
              )
            ) : (
              <p className="settings-info">2FA is already enabled for your account.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;



